/**
 * پیش‌نویسِ خودکارِ فرم‌ها (v2.142.0).
 *
 * کاربر (۱۴۰۵/۰۷/۰۷): «وقتی کسی دارد اطلاعات وارد می‌کند و ما آپدیت می‌دهیم، امکانِ ذخیره ندارد… اطلاعاتی که وارد
 * کرده ولی سیو نشده به‌عنوانِ پیش‌نویس بیاید، یا عیناً همان صفحه بیاید بالا که ادامه دهد — مخصوصاً بخشِ تولید».
 *
 *   const d = useDraft("ops:1405-0612:12", rows, { isEmpty, enabled });
 *   <DraftBar draft={d} onRestore={(v) => setRows(v)} />
 *   // بعد از ذخیرهٔ موفق:  d.clear()
 *
 * هر تغییر (با ۴۰۰ms تأخیر) در localStorageِ همین مرورگر، جدا برای هر کاربر، ذخیره می‌شود؛ رویدادِ سراسریِ
 * `gx:flush-drafts` (اعلانِ بروزرسانی) و `beforeunload` فوراً می‌نویسند. تا کاربر دربارهٔ پیش‌نویسِ قبلی تصمیم نگرفته
 * (ادامه/دورریختن)، چیزی رویش نوشته نمی‌شود. عمر: ۷ روز.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const PREFIX = "gx-draft:";
const MAX_AGE = 7 * 24 * 3600 * 1000;

function userId() {
  try { return JSON.parse(localStorage.getItem("sc_user") || "{}").id || "anon"; } catch { return "anon"; }
}
const fullKey = (key) => `${PREFIX}${userId()}:${key}`;

export function readDraft(key) {
  try {
    const d = JSON.parse(localStorage.getItem(fullKey(key)) || "null");
    if (!d || !d.at || Date.now() - d.at > MAX_AGE) return null;
    return d;
  } catch { return null; }
}
function writeDraft(key, value) {
  try { localStorage.setItem(fullKey(key), JSON.stringify({ at: Date.now(), path: window.location.pathname + window.location.search, value })); } catch { /* پر/بسته */ }
}
export function removeDraft(key) {
  try { localStorage.removeItem(fullKey(key)); } catch { /* */ }
}
/** همهٔ پیش‌نویس‌هایی که کلیدشان با این پیشوند شروع می‌شود (مثلاً همهٔ سایزهای یک بیجک). */
export function listDrafts(prefix) {
  const out = [];
  try {
    const base = fullKey(prefix);
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(base)) {
        const key = k.slice(fullKey("").length);
        const d = readDraft(key);
        if (d) out.push({ key, ...d });
      }
    }
  } catch { /* */ }
  return out;
}
/** اعلانِ بروزرسانی صدا می‌زند تا همهٔ فرم‌های باز همین حالا پیش‌نویس شوند. */
export function flushAllDrafts() {
  try { window.dispatchEvent(new Event("gx:flush-drafts")); } catch { /* */ }
}

export function useDraft(key, value, { isEmpty = (v) => v == null, enabled = true } = {}) {
  const [pending, setPending] = useState(null);         // پیش‌نویسِ قبلی که هنوز تصمیمش گرفته نشده
  const decided = useRef(false);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {                                     // با عوض‌شدنِ کلید: پیش‌نویسِ همان کلید؟
    decided.current = false;
    const d = enabled && key ? readDraft(key) : null;
    if (d && !isEmpty(d.value)) setPending(d);
    else { setPending(null); decided.current = true; }
  }, [key, enabled]);   // eslint-disable-line

  const save = useCallback(() => {
    if (!enabled || !key || !decided.current) return;
    if (isEmpty(latest.current)) removeDraft(key);
    else writeDraft(key, latest.current);
  }, [key, enabled]);   // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(save, 400);
    return () => clearTimeout(t);
  }, [value, save]);
  useEffect(() => {
    window.addEventListener("gx:flush-drafts", save);
    window.addEventListener("beforeunload", save);
    return () => { window.removeEventListener("gx:flush-drafts", save); window.removeEventListener("beforeunload", save); };
  }, [save]);

  const restore = useCallback(() => {
    const v = pending?.value;
    decided.current = true; setPending(null);
    return v;
  }, [pending]);
  const discard = useCallback(() => {
    if (key) removeDraft(key);
    decided.current = true; setPending(null);
  }, [key]);
  const clear = useCallback(() => {
    if (key) removeDraft(key);
    decided.current = true; setPending(null);
  }, [key]);
  return { pending, restore, discard, clear };
}
